import { ClerkProvider, useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { setAuthTokenGetter } from "./api/client";
import { CLERK_ENABLED, CLERK_PUBLISHABLE_KEY } from "./lib/clerk";
import "./index.css";

// ---- Sentry ----
//
// Initialized as early as possible so module-level errors get
// captured. DSN comes from the build-time env var
// ``VITE_SENTRY_DSN`` (Vite inlines it into the bundle). When the
// var isn't set (local dev, preview builds) we no-op — the SDK is
// safe to leave uninitialized.
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn) {
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || "production",
    // Browser tracing is opt-in. Off by default to keep bundle size
    // tighter; flip to 0.1 once we have enough traffic for it to be
    // useful. Replays + profiling stay off until we explicitly want them.
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stealth-stage admin: we don't need aggressive refetching.
      // Stale time keeps the UI responsive when navigating between
      // the same resource (e.g. flipping into a job detail and back).
      staleTime: 30_000,
      retry: 1,
    },
  },
});

/** Hands Clerk's getToken to the plain-module API client, and holds
 * rendering until Clerk has restored any existing session — so the
 * first /auth/me probe already carries the bearer token instead of
 * bouncing an authenticated user through /login on every reload. */
function ClerkApiBridge({ children }: { children: ReactNode }) {
  const { getToken, isLoaded } = useAuth();
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm text-graphite-soft">
        Loading…
      </div>
    );
  }
  // Assign during render (idempotent) so the getter exists before any
  // child query effect fires its first fetch.
  setAuthTokenGetter(() => getToken());
  return <>{children}</>;
}

/** ClerkProvider when enabled; transparent pass-through when the env
 * override explicitly disables Clerk (legacy-login escape hatch). */
function AuthShell({ children }: { children: ReactNode }) {
  if (!CLERK_ENABLED) return <>{children}</>;
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/login">
      <ClerkApiBridge>{children}</ClerkApiBridge>
    </ClerkProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing #root element in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen bg-paper p-12 text-graphite">
          <h1 className="font-mono text-xl font-bold">Something went wrong.</h1>
          <p className="mt-2 text-sm text-graphite-soft">
            We've been notified. Try refreshing — if it sticks, ping support so we can take a look.
          </p>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthShell>
            <App />
          </AuthShell>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
